/**
 * UserMessage component displays user messages with avatar and timestamp.
 * Right-aligned with primary color styling.
 */
import type { ChatMessage } from "@nucleus/shared-types";
import { formatDistanceToNow } from "date-fns";

import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

interface UserMessageProps {
	/** The user message to display */
	message: ChatMessage;
	/** Whether to show the avatar */
	showAvatar?: boolean;
}

/**
 * Get user initials from name for avatar display.
 * Falls back to first letter of email if name not available.
 * @param name - User's full name or null
 * @param email - User's email address
 * @returns Two-character initials string
 */
function getUserInitials(name: string | null, email: string): string {
	if (name) {
		const parts = name.split(" ");
		const firstPart = parts[0];
		const lastPart = parts[parts.length - 1];
		if (parts.length >= 2 && firstPart && lastPart) {
			const firstChar = firstPart.charAt(0);
			const lastChar = lastPart.charAt(0);
			return `${firstChar}${lastChar}`.toUpperCase();
		}
		return name.slice(0, 2).toUpperCase();
	}
	return email.charAt(0).toUpperCase();
}

/**
 * UserMessage displays a user's message with avatar, content, and timestamp.
 *
 * Features:
 * - Right-aligned layout
 * - User avatar with initials
 * - Relative timestamp (e.g., "2 min ago")
 * - Primary color bubble styling
 * @param props - Component props
 * @param props.message - The message object to display
 * @param props.showAvatar - Whether to show the avatar (false for grouped messages)
 */
export function UserMessage({ message, showAvatar = true }: UserMessageProps) {
	const user = useAuthStore((state) => state.user);

	const initials = user ? getUserInitials(user.name, user.email) : "U";

	return (
		<div className="flex justify-end gap-3">
			{/* Message content - right-aligned */}
			<div className="flex max-w-[70%] flex-col items-end gap-1">
				{/* Message bubble */}
				<div className="rounded-2xl bg-primary px-4 py-2.5 text-primary-foreground">
					<p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
				</div>

				{/* Timestamp */}
				<time className="text-xs text-muted-foreground">
					{formatDistanceToNow(message.createdAt, { addSuffix: true })}
				</time>
			</div>

			{/* Avatar */}
			<div
				className={cn(
					"flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground",
					!showAvatar && "invisible"
				)}
			>
				{initials}
			</div>
		</div>
	);
}
